/**
 * What a set of marks stands for, which is not the same as how many of them there are.
 *
 * Its own module because the answer depends on something only the host knows, and because the
 * arithmetic has a wrong version that is easy to write and hard to see: the contents of a region,
 * or of the whole canvas, look like a count of marks until something is closed.
 */

import type { NetworkGraph, NetworkNode, NetworkNodeId } from '../types';

/**
 * What a mark is hiding: how much of it, and the picture where there is one worth drawing.
 *
 * A count beside the graph rather than a graph alone, because the count survives where the
 * picture does not. A few hundred marks in a two-hundred-pixel box is a grey disc that answers
 * none of the questions a thumbnail is for, so a host is expected to withhold the graph there -
 * and a tooltip that read the count off the graph would then say nothing about a group that had
 * just folded away half the estate.
 */
export type NetworkFolded = {
  /** How many marks are behind this one, not counting this one. */
  foldedCount: number;
  /**
   * How many marks this one accounts for on screen: `foldedCount`, plus itself where it is a mark
   * in its own right.
   *
   * A second count because the two answer different questions and one of them cannot be derived.
   * A closed group's own tooltip wants what is behind it; the region *containing* that closed
   * group wants what it stands for, and whether the mark counts as one of those is something only
   * the host knows - a group a rule invented is scaffolding, and a group that is also a component
   * is not.
   */
  accountsFor: number;
  /** The graph to draw inside the tooltip, or `null` where there is none worth reading. */
  graph: NetworkGraph | null;
};

/**
 * How many marks a set of them stands for, counted through them rather than by counting them.
 *
 * `markIndices.length` is how many marks are *drawn*, which stops being the same number the
 * moment one of them is closed. Both readers of this had the wrong version: a region reported a
 * closed subgroup of ten as one component, so collapsing a nested group changed the number its
 * parent reported; and the canvas's own accessible name counted every closed group as a
 * component, announcing "7 components" for seven groups standing for twenty-eight.
 *
 * A mark nothing is folded behind counts as one, which is the case for every mark on a host that
 * does not collapse at all. An index the graph does not contain counts as nothing - a hover
 * indexes into the graph it was resolved against, and a rebuild under a stationary pointer leaves
 * one render reading the old indices before the reset clears them.
 */
export const toAccountedMarkCount = (
  markIndices: readonly number[],
  nodes: readonly NetworkNode[],
  foldedBehind: ((nodeId: NetworkNodeId) => NetworkFolded | null) | undefined
): number =>
  markIndices.reduce((total, index) => {
    const node = nodes[index];
    if (node === undefined) return total;
    return total + (foldedBehind?.(node.id)?.accountsFor ?? 1);
  }, 0);
